import { Component, inject, input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
    IonButton,
    IonCheckbox,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonItem,
    IonList,
    IonToolbar,
    ModalController,
} from '@ionic/angular';
import {
    AnnouncementAssignableUserRoles,
    assignableCompositeRoles,
    AssignableRoles,
    BasicAssignableUserRole,
    ChatAssignableUserRoles,
    CompositeRole,
    getAssignableRoles,
    isRoleAssignmentValid,
    KnowledgeAssignableUserRoles,
    MeetingAssignableUserRoles,
    NotesAssignableUserRoles,
} from '../../entities/user-roles';
import { ModalConfig } from '../../../common/entities/modal/modal-config';
import { ModalTyp } from '../../../common/entities/modal/modal-typ';
import { ButtonConfig } from '../../../common/entities/modal/modal-button';
import { ModalAlertService } from '../../../common/services/modal';
import { ConfigService } from '../../../config/services/config.service';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { LoadingComponent } from '../../../common/components/loading/loading.component';
import { Logger, LoggingService } from '../../../logging/logging.service';

class SelectableRoleListItem {
    name: AssignableRoles;
    isChecked: boolean;
    isVisible: boolean;
    isDisabled: boolean;

    constructor(name: AssignableRoles, isChecked: boolean, isVisible: boolean, isDisabled: boolean) {
        this.name = name;
        this.isChecked = isChecked;
        this.isVisible = isVisible;
        this.isDisabled = isDisabled;
    }
}

@Component({
    selector: 'lib-role-list-modal',
    templateUrl: './role-list-modal.component.html',
    styleUrls: ['./role-list-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        LoadingComponent,
        IonHeader,
        IonToolbar,
        IonButton,
        IonIcon,
        IonContent,
        IonItem,
        IonList,
        IonCheckbox,
        IonFooter,
        TranslatePipe,
    ],
})
export class RoleListModalComponent implements OnInit {
    isLoading = true;
    isLoadingSuccess = false;
    title = 'ROLE.SELECT_SEVERAL';
    alreadyAssignedUserRoles = input<SelectableRoleListItem[]>([]);
    roleSelection: SelectableRoleListItem[];
    features = inject(ConfigService).frontendConfig().features;
    protected readonly configService = inject(ConfigService);
    backendFeatures = this.configService.config()?.configuration.features;
    protected readonly log: Logger;

    constructor(
        protected readonly modalAlertService: ModalAlertService,
        protected readonly modalController: ModalController,
        private loggingService: LoggingService,
    ) {
        this.log = this.loggingService.getLogger(this.constructor.name);
    }

    async ngOnInit(): Promise<void> {
        await this.initRoleList();
    }

    async initRoleList(): Promise<void> {
        this.isLoading = true;
        this.isLoadingSuccess = false;
        if (!this.roleSelection) {
            const assignableRoles: AssignableRoles[] = Object.values(BasicAssignableUserRole);
            if (this.backendFeatures.chat?.enabled) {
                assignableRoles.push(...Object.values(ChatAssignableUserRoles));
            }
            if (this.backendFeatures.note?.enabled) {
                assignableRoles.push(...Object.values(NotesAssignableUserRoles));
            }
            if (this.backendFeatures.announcement?.enabled) {
                assignableRoles.push(...Object.values(AnnouncementAssignableUserRoles));
            }
            if (this.features.knowledge?.enabled) {
                assignableRoles.push(...Object.values(KnowledgeAssignableUserRoles));
            }
            if (this.backendFeatures.course?.enabled) {
                assignableRoles.push(...Object.values(MeetingAssignableUserRoles));
            }
            this.roleSelection = assignableRoles.map((x) => new SelectableRoleListItem(x, false, true, true));
        }
        try {
            await this.updateListTable(this.alreadyAssignedUserRoles().map((x) => x.name));
            this.isLoadingSuccess = true;
        } catch (err) {
            this.log.error('[initRoleList] error');
            this.log.error(err);
            this.isLoadingSuccess = false;
        } finally {
            this.isLoading = false;
        }
    }

    async updateListTable(selected: AssignableRoles[]): Promise<void> {
        let assignableUserRoles: AssignableRoles[];
        let selectedCompositeRoles: CompositeRole[];
        const hasAtLeastOneBasicRole = [
            // Base Composite Roles
            BasicAssignableUserRole.ANALYST,
            BasicAssignableUserRole.CAREGIVER,
            BasicAssignableUserRole.SUPERVISOR,
            BasicAssignableUserRole.USER_MANAGER,
            BasicAssignableUserRole.CATALOG_MANAGER,
            BasicAssignableUserRole.PATIENT,
        ].some((r) => selected.includes(r));
        if (!hasAtLeastOneBasicRole) {
            assignableUserRoles = getAssignableRoles(
                [],
                this.features,
                this.configService.config()?.configuration.features,
            );
            selectedCompositeRoles = assignableCompositeRoles.filter((x) => [].includes(x.roleName));
        } else {
            assignableUserRoles = getAssignableRoles(
                selected,
                this.features,
                this.configService.config()?.configuration.features,
            );
            selectedCompositeRoles = assignableCompositeRoles.filter((x) => selected.includes(x.roleName));
        }
        for (const roleListItem of this.roleSelection) {
            roleListItem.isChecked = hasAtLeastOneBasicRole
                ? (!!selected.find((x) => x === roleListItem.name) &&
                      !!assignableUserRoles.find((x) => x === roleListItem.name)) ||
                  selectedCompositeRoles
                      .map((x) => x.compositeRoleNames)
                      .flat()
                      .includes(roleListItem.name)
                : false;
            roleListItem.isDisabled = !assignableUserRoles.find((x) => x === roleListItem.name);
        }
    }

    async onItemSelection(): Promise<void> {
        await this.updateListTable(this.roleSelection.filter((x) => x.isChecked === true).map((x) => x.name));
    }

    async dismissModal(): Promise<void> {
        await this.modalController.dismiss();
    }

    async returnSelectedGroups(): Promise<void> {
        const modalConfig = new ModalConfig();
        modalConfig.modalTyp = ModalTyp.INFORMATION;
        modalConfig.title = 'Rolle ändern';
        modalConfig.titleIcon = 'warning-outline';
        modalConfig.description = `Möchten Sie die Rollenzuordnung tatsächlich ändern? Hierdurch können für diesen Benutzer Einschränkungen bei der Bedienung auftreten.`;

        modalConfig.buttonRight = new ButtonConfig();
        modalConfig.buttonRight.buttonText = 'Änderung \n bestätigen';
        modalConfig.buttonRight.buttonColor = 'primary';
        const action = await this.modalAlertService.showModal(modalConfig);
        if (action && action.action === 'right') {
            const result: { role: string }[] = [];
            for (const selectedRole of this.roleSelection.filter((i) => i.isChecked)) {
                result.push({ role: selectedRole.name });
            }
            await this.modalController.dismiss(result);
        }
    }

    isButtonDisabled(): boolean {
        // At least one role must be selected for the button to be clickable
        return !isRoleAssignmentValid(
            this.roleSelection.filter((x) => x.isChecked).map((x) => x.name),
            this.features,
            this.configService.config()?.configuration.features,
        );
    }
}
